import { useMemo } from 'react';
import { t } from '@lingui/macro';

import { useId } from '../../page-framework/panel/Panel';
import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    LINE_CHART_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import PanelActions from '../../page-framework/panel/PanelActions';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import ChartTypeForm from '../../charts/ipm/ChartTypeForm';
import { useMetricColor } from '../../../utilities/ColorUtilities';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { interfaceFilterId, timeRangeFilterId } from '../../filters';
import useChartDisplayType from '../../../utilities/UseChartDisplayType';

const title = t`Interface Errors`;

const dataSource = {
    url: '/query/chart',
    params: {
        type: 'interface',
        metrics: [
            'statusInterfaceErrors',
            'errorsIn',
            'errorsOut',
            'errorsInCount',
            'errorsOutCount',
            'errorsInPercent',
            'errorsOutPercent',
        ],
    },
};

const RATE = 'RATE';
const COUNT = 'COUNT';
const PERCENT = 'PERCENT';

const typeConfig = {
    [RATE]: {
        formatter: FORMATTER_TYPES.LOG,
        yAxisTitle: t`Errors/min`,
        units: ` ${t`Errors/min`}`,
        inMetric: 'errorsIn',
        outMetric: 'errorsOut',
    },
    [COUNT]: {
        formatter: FORMATTER_TYPES.LOG,
        yAxisTitle: t`Errors`,
        units: ` ${t`Errors`}`,
        inMetric: 'errorsInCount',
        outMetric: 'errorsOutCount',
    },
    [PERCENT]: {
        formatter: FORMATTER_TYPES.PERCENT,
        forceDecimals: true,
        yAxisTitle: t`Errors (%)`,
        units: '%',
        inMetric: 'errorsInPercent',
        outMetric: 'errorsOutPercent',
    },
};

function useChartConfig(type) {
    const inColor = useMetricColor('networkIn');
    const outColor = useMetricColor('networkOut');
    const config = typeConfig[type];

    return useMemo(
        () => ({
            yAxis: [
                {
                    title: {
                        text: config.yAxisTitle,
                    },
                },
            ],
            tooltip: BuiltInTooltip,
            showGranularity: true,
            statusMetric: 'statusInterfaceErrors',
            chartTrends: [
                {
                    formatter: config.formatter,
                    units: config.units,
                    chartType: 'spline',
                    key: config.inMetric,
                    label: t`In`,
                    color: inColor,
                    multiplier: 1,
                    forceDecimals: config.forceDecimals === true,
                },
                {
                    formatter: config.formatter,
                    units: config.units,
                    chartType: 'spline',
                    key: config.outMetric,
                    label: t`Out`,
                    color: outColor,
                    multiplier: 1,
                    forceDecimals: config.forceDecimals === true,
                },
            ],
        }),
        [config, inColor, outColor]
    );
}

const selections = [
    { value: RATE, label: t`Rate` },
    { value: COUNT, label: t`Count` },
    { value: PERCENT, label: '%' },
];

function InterfaceErrorsPanel() {
    const id = useId();
    const pageStateKey = `${id}-ChartType`;
    const [chartDisplayType, setChartDisplayType] = useChartDisplayType(pageStateKey, COUNT);
    const data = usePanelDataService(dataSource);
    const chartConfig = useChartConfig(chartDisplayType);
    const chartData = data?.data?.[0];

    if (!data) return null;

    return (
        <>
            <PanelActions>
                <ChartTypeForm value={chartDisplayType} onChangeType={setChartDisplayType} selections={selections} />
            </PanelActions>
            <PerformanceChart chartDef={chartConfig} data={chartData} />
        </>
    );
}

InterfaceErrorsPanel.propTypes = panelPropTypes;

InterfaceErrorsPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'InterfaceErrors',
    catalogEntry: {
        category: PANEL_CATEGORIES.INTERFACE,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`Errors trend for a selected interface`,
        previewImage: LINE_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, interfaceFilterId],
        optional: [],
    },
})(InterfaceErrorsPanel);
